import React from "react";
import {
    addMouseExitHandler,
    addMousePositionHandler,
    movementClientHandlers,
    removeMouseExitHandler,
    removeMousePositionHandler,
} from "webapp/chrome/smart/swipe-out-controller/swipe-out-controller";
import { consumeAsSideEffect } from "./consume-as-side-effect";

type OnMoveHandler = (loc: [number, number], delta: [number, number]) => void;
type OnEndHandler = () => void;

// The output of this hook are mouse and touch event handlers to trigger the
// "swipe out" controller. Use them directly in component props.
//
// For example,
// const callbacks = useSwipeOutController(onMove, onEnd);
// <SwipeStackStyle {...callbacks} />
export function useSwipeOutController(onMove: OnMoveHandler, onEnd: OnEndHandler) {
    const [touchStart, setTouchStart] = React.useState<[number, number] | undefined>();
    const [exitState, setExitState] = React.useState<number>(0);
    const rafHandle = React.useRef<number | null>(null);
    const positionState = React.useRef({ clientX: 0, clientY: 0 });

    // Certain browsers (Looking at you, Chrome) spam touch events to a point
    // that it becomes a rendering performance issue. To work around this, we
    // trap the touch events and keep them in a ref that later gets consulted
    // by a requestAnimationFrame handler.
    const insideRaf = React.useCallback(() => {
        rafHandle.current = null;
        if (touchStart === undefined) return;

        const { clientX, clientY } = positionState.current;
        onMove([clientX, clientY], [clientX - touchStart[0], clientY - touchStart[1]]);
    }, [onMove, touchStart, positionState, rafHandle]);

    const onInternalMove = React.useCallback(
        (clientX: number, clientY: number) => {
            const { current } = positionState;
            if (touchStart === undefined) {
                setTouchStart([clientX, clientY]);
                current.clientX = clientX;
                current.clientY = clientY;
                return;
            }

            current.clientX = clientX;
            current.clientY = clientY;
            if (rafHandle.current === null) {
                rafHandle.current = requestAnimationFrame(insideRaf);
            }
        },
        [touchStart, setTouchStart, positionState, rafHandle, insideRaf]
    );

    const onInternalEnd = React.useCallback(() => {
        onEnd();
        setExitState(exitState + 1);
        setTouchStart(undefined);
    }, [onEnd, exitState, setExitState, setTouchStart]);

    React.useEffect(() => {
        addMousePositionHandler(onInternalMove);
        return () => removeMousePositionHandler(onInternalMove);
    }, [onInternalMove]);

    React.useEffect(() => {
        addMouseExitHandler(onInternalEnd);
        return () => removeMouseExitHandler(onInternalEnd);
    }, [onInternalEnd]);

    const eventHandlers = React.useMemo(() => {
        consumeAsSideEffect(exitState);
        return movementClientHandlers();
    }, [exitState]);
    return eventHandlers;
}
